import { format, parseISO } from 'date-fns';
import i18next from 'i18next';
import { DATE_FORMATS } from './date';
import { formatTimePrefix, formatTimingAsHumanReadable, getTimeSortKey, type TimingRepeat } from './formatTiming';

// ============================================================================
// Types
// ============================================================================

export interface ScheduleItemLike {
  timing: TimingRepeat | null;
}

export type GroupLabel = 'daily' | 'weeklyBiWeekly' | 'lessOften' | 'upcoming';

export const GROUP_ORDER: GroupLabel[] = ['daily', 'weeklyBiWeekly', 'lessOften', 'upcoming'];

/** Translate a GroupLabel to a display string. */
export function translateGroupLabel(label: GroupLabel): string {
  switch (label) {
    case 'daily':
      return i18next.t('common:scheduleGroup.daily');
    case 'weeklyBiWeekly':
      return i18next.t('common:scheduleGroup.weeklyBiWeekly');
    case 'lessOften':
      return i18next.t('common:scheduleGroup.lessOften');
    case 'upcoming':
      return i18next.t('common:scheduleGroup.upcoming');
    default:
      return label;
  }
}

// ============================================================================
// Grouping
// ============================================================================

/** Approximate days per period unit, for bucketing cadence length. */
const UNIT_DAYS: Record<string, number> = { h: 0, d: 1, wk: 7, mo: 30, a: 365 };

const BIWEEKLY_DAYS = 14;

export function getScheduleGroup(item: ScheduleItemLike): GroupLabel {
  const timing = item.timing;
  if (!timing) return 'upcoming';

  // Bucket by cadence length, not by unit string — "every 3 days" belongs
  // with the weekly cadences (its row label says "Every 3 days"), an hourly
  // repeat recurs daily, and a monthly or yearly item under "Weekly &
  // Bi-Weekly" would contradict its own label.
  const cadenceDays = (timing.period ?? 1) * (UNIT_DAYS[timing.periodUnit ?? 'd'] ?? 1);
  if (cadenceDays <= 1) return 'daily';
  if (cadenceDays <= BIWEEKLY_DAYS) return 'weeklyBiWeekly';
  return 'lessOften';
}

// ============================================================================
// Labels
// ============================================================================

/** The cadence half of the label — the repeat sentence alone, with no due date and no PRN qualifier. */
function getCadenceLabel(timing: TimingRepeat | null): string | undefined {
  if (timing) {
    const periodUnit = timing.periodUnit;
    // Only a period-1 'd' cadence is "daily" — an every-N-days interval
    // (period ≥ 2) must render its full sentence ("Every 3 days"), not
    // "Daily".
    if ((!periodUnit || periodUnit === 'd') && (timing.period ?? 1) === 1) {
      if (timing.timeOfDay && timing.timeOfDay.length > 0) {
        const times = timing.timeOfDay.map(formatTimePrefix).filter((t): t is string => Boolean(t));
        if (times.length >= 2) {
          return `${i18next.t('common:timesPerDay', { count: times.length })} (${times.join(', ')})`;
        }
        // Times the formatter could not read leave nothing to say; fall through to the cadence word.
        if (times.length === 1) return times[0];
      }
      if (timing.when && timing.when.length > 0) {
        return formatTimingAsHumanReadable(timing);
      }
      return i18next.t('common:daily');
    }
    return formatTimingAsHumanReadable(timing);
  }

  return undefined;
}

/**
 * Builds a human-readable schedule label for a task.
 * For daily tasks: shows specific time or time-of-day.
 * For weekly/monthly: shows full schedule description.
 * When timing is null, falls back to a translated due date label if provided.
 *
 * "As needed" (PRN) is an independent axis, so it qualifies whatever frequency the task carries
 * ("Every 4 hours as needed") and, with no frequency, carries the due date of the one-off it marks
 * ("As needed, due Jan 1, 2027") (#10259).
 */
export function getScheduleLabel(
  timing: TimingRepeat | null,
  dueDate?: string | null,
  asNeeded?: boolean,
): string | undefined {
  const cadence = getCadenceLabel(timing);
  if (asNeeded) {
    // A cadence makes the date bound a course end rather than a due date, so it is left out. With no
    // cadence the item is a one-off the picker let a clinician date, and the date is that due date.
    if (cadence) return i18next.t('common:asNeededQualifier', { schedule: cadence });
    return dueDate
      ? i18next.t('common:asNeededDueDate', { date: format(parseISO(dueDate), DATE_FORMATS.DATE) })
      : i18next.t('common:asNeeded');
  }
  if (cadence) return cadence;
  if (dueDate) {
    return i18next.t('common:dueDateLabel', { date: format(parseISO(dueDate), DATE_FORMATS.DATE) });
  }
  return undefined;
}

// ============================================================================
// Sorting
// ============================================================================

/**
 * Sort key for ordering tasks within a group.
 * Daily tasks sort by time of day; items without timing sort last.
 */
export function getSortKey(item: ScheduleItemLike): number {
  const timing = item.timing;
  if (!timing) return 9999;

  if (timing.timeOfDay && timing.timeOfDay.length > 0) {
    return getTimeSortKey(timing.timeOfDay[0]!, 1400);
  }

  if (timing.when && timing.when.length > 0) {
    return getTimeSortKey(timing.when[0]!, 800);
  }

  return 1400;
}
